
import { action, makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'
import { DepartmentsAccessStore } from '../store'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'
import { getRole } from '@evertel/utils'

class DepartmentsAccessController {
    userId: number = undefined

    constructor(
        private api: Api,
        private departmentsAccessStore: DepartmentsAccessStore,
        private session: SessionService
    ) {
        makeAutoObservable(this, {init: action})
    }

    init(userId: number) {
        if (!userId) return

        this.userId = userId

    }

    get departmentsAccess() {
        return this.departmentsAccessStore.find(da => da.blueUserId === this.userId)
    }

    departmentAccess(deptId) {
        return this.departmentsAccess?.find(da => da.departmentId === deptId)
    }

    departmentRole(deptId) {
        const dA = this.departmentAccess(deptId)
        if (!dA) return undefined
        return getRole(dA.roles)
    }

    departmentsWithRole(roleName: 'executive' | 'management' | 'employee' | 'guest') {
        return this.departmentsAccess
            ?.filter(da => getRole(da.roles) === roleName)
            .map(da => da.departmentId) ?? []
    }

    get primaryDepartmentsAccess() {
        return this.departmentsAccess.find(da => da.isPrimary === true)
    }

    get selectedDepartmentsAccess() {
        return this.departmentAccess(this.session.selectedDepartmentId)
    }

    get selectedDepartmentRole() {
        return this.departmentRole(this.session.selectedDepartmentId)
    }

    async setRole(departmentId: number, role: string) {
        const da = await this.api.Routes.Department.putUsersRoles(departmentId, this.userId, role)

        runInAction(() => {
            this.departmentsAccessStore.update(da)
        })
    }

    async setVerified(departmentId: number, isVerified: boolean) {
        const details = await this.api.Routes.Department.putUsersVerify(departmentId, this.userId, isVerified)

        runInAction(() => {
            this.departmentsAccessStore.update(details)
        })
    }

    async addDepartmentAccess(departmentId: number) {
        const departmentsAccess = await this.api.Routes.Department.putUsersById(departmentId, this.userId)

        runInAction(() => {
            this.departmentsAccessStore.update(departmentsAccess)
        })
    }

    async removeDepartmentAccess(departmentId: number) {
        const result = await this.api.Routes.Department.delUsersById(departmentId, this.userId)

        const deptAccess = this.departmentAccess(departmentId)

        if (result.count) {
            runInAction(() => {
                this.departmentsAccessStore.deleteById(deptAccess.id)
            })
        }

    }
}

decorate(injectable(), DepartmentsAccessController)
decorate(inject(Api), DepartmentsAccessController, 0)
decorate(inject(DepartmentsAccessStore), DepartmentsAccessController, 1)
decorate(inject(SessionService), DepartmentsAccessController, 2)

export { DepartmentsAccessController }
